import { PaymentSchedule } from '@/components/Payments/PaymentSchedule'
import { JobCostingPanel } from '@/components/JobCosting/JobCostingPanel'
import { JobChecklist } from '@/components/Jobs/JobChecklist'
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  X, MapPin, ArrowRight, Trophy, Send,
  Calendar, StickyNote, ChevronDown, ChevronUp, DollarSign,
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import { pastelHex, useServiceCategories } from '@/lib/serviceCatalog'
import { formatTransitionError } from '@/lib/transitionError'
import { useUIStore } from '@/stores/uiStore'
import { serviceTypeTargets, useSetServiceType } from '@/hooks/useWorkMutations'
import { formatCurrency } from '@/utils/formatting'
import { fmtDate } from '@/lib/datetime'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { cn } from '@/lib/utils'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { Button } from '@/components/ui/Button'
import { SafeAreaBottom } from '@/components/ui/SafeAreaBottom'
import type { Record_, ActivityEntry } from './types'
import { STAGE_BY_ID, STAGE_ADVANCE_CAPABILITY, STAGE_EDIT_CAPABILITY, stageStateLine, displayStageOf, stageWithDetail, nextStepHint } from './stages'
import { usePermissions } from '@/hooks/usePermission'
import { useOnline } from '@/hooks/useOnline'
import { useToastsClearOfSidePanel, SIDE_PANEL_WIDTH } from '@/components/ui/Toast'
import { DrawerOfflineNotice, OfflineActions, OFFLINE_ACTION_REASON } from '@/components/ui/OfflineActions'
import { ContactActions, mapsHref } from './ContactActions'
import { JourneyStepper } from './JourneyStepper'
import { QuoteModule } from './QuoteModule'
import { DrawerHeaderActions, documentSlots, primaryModuleFor, type DocumentActionHandlers, type DocumentSlots } from './DrawerHeaderActions'
import { JobFieldNotes } from '@/components/Jobs/JobFieldNotes'
import { DealNotes } from './DealNotes'
import { JobModule } from './JobModule'
import { AssigneesModule } from './AssigneesModule'
import { InvoiceModule } from './InvoiceModule'
import { ActivityTimeline } from './ActivityTimeline'
import { PhotosModule, shouldShowPhotos } from './PhotosModule'
import { InspectionsModule } from './InspectionsModule'
import { DocumentsModule } from './DocumentsModule'
import { ServiceTypeChip } from './ServiceTypeChip'
import { useShowMoney } from './moneyVisibility'
import { ErrorState } from '@/components/ui/QueryState'

// ─── LeadContextBlock ─────────────────────────────────────────────────────────

interface LeadContextBlockProps {
  record: Record_
}

function LeadContextBlock({ record }: LeadContextBlockProps) {
  const [notesOpen, setNotesOpen] = useState(false)

  const hasAny = record.closingDate || record.notes || record.dealBudget

  if (!hasAny) return null

  return (
    <div
      style={{
        borderRadius: 'var(--midnight-r-lg, 24px)',
        background: 'rgb(var(--bg-rgb) / 0.25)',   // ds-tile-inset — no backdrop-filter
        border: '0.5px solid var(--midnight-border, rgb(var(--fg-rgb) / 0.08))',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <p
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--midnight-text3, rgb(var(--fg-rgb) / 0.40))',
          margin: 0,
        }}
      >
        Lead context
      </p>

      {/* Expected close date */}
      {record.closingDate && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
          }}
        >
          <Calendar style={{ width: 13, height: 13, flexShrink: 0 }} />
          <span>
            Expected close:{' '}
            <span style={{ color: 'rgb(var(--fg-rgb))' }}>
              {fmtDate(record.closingDate, 'MMM d, yyyy')}
            </span>
          </span>
        </div>
      )}

      {/* Budget range */}
      {record.dealBudget && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
          }}
        >
          <DollarSign style={{ width: 13, height: 13, flexShrink: 0 }} />
          <span>
            Budget:{' '}
            <span style={{ color: 'rgb(var(--fg-rgb))' }}>
              {formatCurrency(record.dealBudget.min)} – {formatCurrency(record.dealBudget.max)}
            </span>
          </span>
        </div>
      )}

      {/* Notes — collapsible */}
      {record.notes && (
        <div>
          <button
            type="button"
            onClick={() => setNotesOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              width: '100%',
              textAlign: 'left',
              fontSize: 13,
              color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              transition: 'color 0.2s ease',
            }}
          >
            <StickyNote style={{ width: 13, height: 13, flexShrink: 0 }} />
            <span>Notes</span>
            {notesOpen
              ? <ChevronUp style={{ width: 12, height: 12, marginLeft: 'auto' }} />
              : <ChevronDown style={{ width: 12, height: 12, marginLeft: 'auto' }} />
            }
          </button>
          {notesOpen && (
            <p
              style={{
                marginTop: 8,
                fontSize: 13,
                color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
                lineHeight: 1.6,
                paddingLeft: 20,
                borderLeft: '1px solid var(--midnight-border, rgb(var(--fg-rgb) / 0.08))',
              }}
            >
              {record.notes}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── ServiceTypeField ─────────────────────────────────────────────────────────
// Compact "Service type" select. PATCHes { serviceCategoryId } on the entity
// the record is backed by (deal / quote / job — see serviceTypeTargets), then
// asks the parent to refetch so every view picks up the new type.

interface ServiceTypeFieldProps {
  record: Record_
  onRecordChanged?: () => void | Promise<void>
}

function ServiceTypeField({ record, onRecordChanged }: ServiceTypeFieldProps) {
  const selectId = useId()
  const { active, byId } = useServiceCategories()
  const setServiceType = useSetServiceType()
  const addToast = useUIStore((st) => st.addToast)
  const online = useOnline()
  // undefined = nothing in flight; string | null = the value being saved.
  const [pending, setPending] = useState<string | null | undefined>(undefined)

  useEffect(() => { setPending(undefined) }, [record.id])

  const targets = serviceTypeTargets(record)
  const current = pending !== undefined ? pending : record.serviceCategoryId
  const saving = pending !== undefined

  // Active types, plus the record's current one when it has since been
  // deactivated (or the cache hasn't loaded) so the select never misreports it.
  const options = useMemo(() => {
    const list = active.map((c) => ({ id: c.id, name: c.name, color: c.color }))
    if (current && !list.some((c) => c.id === current)) {
      const known = byId(current)
      list.push({
        id: current,
        name: known
          ? known.name + (known.isActive ? '' : ' (inactive)')
          : (record.service ?? 'Current service type'),
        color: known?.color ?? record.serviceColor ?? 'lavender',
      })
    }
    return list
  }, [active, byId, current, record.service, record.serviceColor])

  const currentColor = current ? options.find((o) => o.id === current)?.color ?? null : null

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value || null
    if (next === (record.serviceCategoryId ?? null)) return
    setPending(next)
    try {
      await setServiceType.mutateAsync({ targets, serviceCategoryId: next })
      await onRecordChanged?.()
    } catch (err) {
      addToast({ type: 'error', title: 'Could not change service type', message: formatTransitionError(err) })
    } finally {
      setPending(undefined)
    }
  }

  return (
    <div
      style={{
        borderRadius: 'var(--midnight-r-lg, 24px)',
        background: 'rgb(var(--bg-rgb) / 0.25)',   // ds-tile-inset — no backdrop-filter
        border: '0.5px solid var(--midnight-border, rgb(var(--fg-rgb) / 0.08))',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
      }}
    >
      <label
        htmlFor={selectId}
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--midnight-text3, rgb(var(--fg-rgb) / 0.40))',
        }}
      >
        Service type
      </label>
      <div style={{ position: 'relative', flex: '1 1 160px', maxWidth: 260 }}>
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 8,
            height: 8,
            borderRadius: 9999,
            pointerEvents: 'none',
            boxSizing: 'border-box',
            // Neutral: pastels mean phases of work, not trades.
            ...(current
              ? { background: 'rgb(var(--fg-rgb) / 0.55)' }
              : { border: '1px solid rgb(var(--fg-rgb) / 0.30)' }),
          }}
        />
        <select
          id={selectId}
          className="input-field"
          value={current ?? ''}
          onChange={handleChange}
          disabled={saving || targets.length === 0 || !online}
          data-needs-network=""
          aria-busy={saving || undefined}
          style={{
            height: 44,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 30,
            paddingRight: 34,
            borderRadius: 9999,
            appearance: 'none',
            WebkitAppearance: 'none',
            colorScheme: 'dark',
            cursor: saving ? 'progress' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          <option value="">None</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        <ChevronDown
          aria-hidden
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 14,
            height: 14,
            color: 'var(--midnight-text3, rgb(var(--fg-rgb) / 0.40))',
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  )
}

// ─── useRecordActivity ────────────────────────────────────────────────────────
// Lazily fetches activity entries for the open record.
// Deals use GET /api/deals/:id/activity; jobs use GET /api/jobs/:id/activity.
// A failed fetch is reported (never shown as "Nothing happened yet").
// A quote with no deal/job feed gets its own steps from the record (UX-021).

/** What the record itself says happened, oldest first: "Quote QT-… sent". */
export function recordFallbackActivity(record: Record_): ActivityEntry[] {
  const out: ActivityEntry[] = []
  const quote = record.quoteNumber ? `Quote ${record.quoteNumber}` : 'Quote'
  if (record.quoteSentAt) out.push({ when: record.quoteSentAt, what: `${quote} sent` })
  if (record.acceptedAt) out.push({ when: record.acceptedAt, what: `${quote} approved` })
  return out
}

function useRecordActivity(record: Record_): { entries: ActivityEntry[]; failed: boolean } {
  const [activity, setActivity] = useState<ActivityEntry[] | null>(null)
  const [failed, setFailed] = useState(false)

  const dealId = record.id.startsWith('deal:') ? record.dealId : undefined
  const jobId = record.jobId

  useEffect(() => {
    let cancelled = false
    setActivity(null)
    setFailed(false)

    if (!dealId && !jobId) return

    const url = dealId
      ? `/api/deals/${dealId}/activity`
      : `/api/jobs/${jobId}/activity`

    apiClient
      .get(url)
      .then((res) => {
        if (!cancelled) setActivity(res.data?.data ?? [])
      })
      .catch(() => {
        if (!cancelled) { setActivity([]); setFailed(true) }
      })

    return () => {
      cancelled = true
    }
  }, [record.id, dealId, jobId])

  const entries = !dealId && !jobId ? recordFallbackActivity(record) : (activity ?? [])
  return { entries, failed }
}

// ─── Drawer pieces ─────────────────────────────────────────────────────────────
//
// One body, two shells (UX-025 / UX-036 / UX-060):
//   • ≥1024px  — a side panel: header (name + one close) / scrolling body /
//                pinned outcome CTA. The outer panel never scrolls; only the
//                body does, and modules never shrink (flex: none), so nothing
//                is squashed into clipped strips.
//   • <1024px  — a BottomSheet: its title is the customer and it owns the one
//                close button; the CTA goes in the sheet's pinned `footer`, so
//                Start work / Mark complete is visible without scrolling.

interface DrawerActions extends DocumentActionHandlers {
  /** Called after a successful write that changes the underlying record
   *  (e.g. photo upload). The parent refetches so photoCount etc. refresh. */
  onRecordChanged?: () => void | Promise<void>
}

export { primaryModuleFor }

function StageHero({ record }: { record: Record_ }) {
  // The lane's colour and icon; the title is the record's own status (UX-183).
  const currentStage = displayStageOf(record.stage)
  const deep = 'var(--c-deep)'
  const showMoney = useShowMoney()
  const line = stageStateLine(record, { money: showMoney })
  return (
    <div
      className={`ds-pastel-card tn-${currentStage.tone}`}
      style={{ borderRadius: 20, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}
    >
      <span
        aria-hidden
        style={{
          width: 36, height: 36, borderRadius: 9999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgb(var(--fg-rgb) / 0.5)', boxShadow: 'inset 0 1px 0 rgb(255 255 255 / calc(0.7 * var(--gloss)))', color: deep, flexShrink: 0,
        }}
      >
        {React.createElement(STAGE_BY_ID[record.stage].icon, { style: { width: 17, height: 17 } })}
      </span>
      {/* minWidth 0 so the label wraps instead of pushing past the edge. */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 16, fontWeight: 600, color: deep, lineHeight: 1.25, margin: 0 }}>{stageWithDetail(record.stage)}</p>
        {line && <p style={{ fontSize: 13, color: deep, opacity: 0.8, marginTop: 2, lineHeight: 1.35 }}>{line}</p>}
      </div>
      {showMoney && record.total > 0 && (
        <span
          style={{
            fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 700, letterSpacing: '-0.03em',
            fontVariantNumeric: 'tabular-nums', color: deep, flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          {formatCurrency(record.total)}
        </span>
      )}
    </div>
  )
}

function FixtureBody({ jobId, costing }: { jobId: string; costing: boolean }) {
  return <div className="ds-drawer-body" data-testid="drawer-body">
    <p>Demo — edits are temporary.</p>
    {costing && <JobCostingPanel jobId={jobId} />}
    <InspectionsModule jobId={jobId} fixture />
  </div>
}

function DrawerBody({ record, actions, slots, inlineActions, compact }: {
  record: Record_
  actions: DrawerActions
  slots: DocumentSlots
  /** Side panel: icons stay on the card. Phone sheet: they live in the header. */
  inlineActions: boolean
  compact: boolean
}) {
  const { entries: activity, failed: activityFailed } = useRecordActivity(record)
  const recordWithActivity = useMemo(() => ({ ...record, activity }), [record, activity])
  const { onRecordChanged } = actions
  const primary = slots.primary

  // Crew start and finish jobs but don't edit, reschedule, staff or re-file them.
  const perms = usePermissions()
  const canEditJobs = perms.can('jobs.edit')
  const canEditRecord = perms.can(STAGE_EDIT_CAPABILITY[record.stage])
  const quote = slots.quote ? (
    <QuoteModule
      key="quote"
      inlineActions={inlineActions}
      {...slots.quote}
    />
  ) : null
  const job = slots.job ? (
    <React.Fragment key="job">
      <JobModule
        inlineActions={inlineActions}
        {...slots.job}
      />
      {record.jobId && perms.can('payments.view') && <PaymentSchedule jobId={record.jobId} />}
      {record.jobId && perms.can('jobs.view') && <JobFieldNotes jobId={record.jobId} canAdd={canEditJobs || perms.can('jobs.complete')} />}
      {record.jobId && <JobChecklist key={record.jobId} jobId={record.jobId} />}
      {record.jobId && <AssigneesModule jobId={record.jobId} onChanged={onRecordChanged} readOnly={!canEditJobs} />}
    </React.Fragment>
  ) : null
  // The invoice (or receipt) is money: Crew see the stage ("Paid") in the
  // hero and journey, never a total or balance (the server strips them to 0).
  const invoice = slots.invoice ? (
    <InvoiceModule
      key="invoice"
      inlineActions={inlineActions}
      {...slots.invoice}
    />
  ) : null
  const lead = (record.stage === 'LEAD' || record.stage === 'QUALIFIED')
    ? <LeadContextBlock key="lead" record={record} />
    : null

  // The stage's own module first; the rest keep their pipeline order.
  const byKind = { lead, quote, job, invoice }
  const rest = (['lead', 'invoice', 'job', 'quote'] as const).filter((k) => k !== primary).map((k) => byKind[k])
  const ordered = [byKind[primary], ...rest]

  return (
    <div className="ds-drawer-body" data-testid="drawer-body" data-primary-module={primary}>
      {/* Address wraps (never clipped) and opens directions. */}
      {record.address && record.address !== '—' && (
        <p style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 14, color: 'var(--midnight-text2)', margin: 0 }}>
          <MapPin style={{ width: 13, height: 13, flexShrink: 0, marginTop: 3 }} aria-hidden />
          <a href={mapsHref(record.address)} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none', overflowWrap: 'anywhere' }}>
            {record.address}
          </a>
        </p>
      )}

      <DrawerOfflineNotice />

      <ContactActions record={record} />

      <StageHero record={record} />

      <div style={{ borderRadius: 20, background: 'rgb(var(--bg-rgb) / 0.25)', border: '0.5px solid var(--midnight-border)', padding: compact ? '4px 16px' : 20 }}>
        <JourneyStepper record={record} collapsible={compact} />
      </div>

      {/* Offline: module actions (send, edit, reminder, payment) are disabled. */}
      <OfflineActions showReason={false}>{ordered}</OfflineActions>

      {/* Notes stay on the record at every stage, not only while it is a lead. */}
      {record.dealId && perms.can('opportunities.view') && (
        <DealNotes dealId={record.dealId} canEdit={perms.can('opportunities.edit')} />
      )}

      {/* Service type — change which trade this record is filed under */}
      {canEditRecord && <ServiceTypeField record={record} onRecordChanged={onRecordChanged} />}

      {record.jobId && <InspectionsModule jobId={record.jobId} />}

      {shouldShowPhotos(record.stage) && <PhotosModule record={record} onPhotoUploaded={onRecordChanged} />}

      {(record.quoteId || record.jobId || record.invoiceId) && <DocumentsModule record={record} />}

      {/* Activity timeline — populated by lazy fetch in useRecordActivity */}
      <ActivityTimeline record={recordWithActivity} failed={activityFailed} />
    </div>
  )
}

/**
 * The pinned outcome CTA: one white pill, "Moves to {next}" under it.
 * A quote that hasn't gone out yet leads with "Send quote" (UX-008): the
 * customer can't approve what they haven't seen.
 */
function DrawerCta({ record, onAdvance, isAdvancing, onSendQuote }: {
  record: Record_
  onAdvance: (id: string) => void
  isAdvancing: boolean
  onSendQuote?: (record: Record_) => void
}) {
  const currentStage = STAGE_BY_ID[record.stage]
  const nextStage = currentStage.next ? STAGE_BY_ID[currentStage.next] : null
  const NextIcon = nextStage?.icon
  const perms = usePermissions()
  const online = useOnline()
  const capability = STAGE_ADVANCE_CAPABILITY[record.stage]
  const allowed = capability ? perms.can(capability) : true
  if (record.stage === 'QUOTED' && !record.quoteSentAt && onSendQuote) {
    const canSend = perms.can('quotes.send')
    const why = !canSend ? perms.reason('quotes.send') : !online ? OFFLINE_ACTION_REASON : null
    return (
      <div data-testid="drawer-cta" style={{ width: '100%' }}>
        <Button
          variant="primary"
          size="hero"
          fullWidth
          leadingIcon={<Send style={{ width: 18, height: 18 }} />}
          onClick={() => onSendQuote(record)}
          disabled={!!why}
          aria-describedby={why ? 'drawer-cta-why' : undefined}
        >
          Send quote
        </Button>
        <p id={why ? 'drawer-cta-why' : undefined} style={{ fontSize: 12, textAlign: 'center', color: 'var(--midnight-text3)', marginTop: 6 }}>
          {why ?? 'Not sent yet. Mark it approved once the customer says yes.'}
        </p>
      </div>
    )
  }
  if (!nextStage || !NextIcon) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '6px 0' }}>
        <span style={{ width: 32, height: 32, borderRadius: 9999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(var(--mint-rgb) / 0.14)', color: 'var(--mint, var(--mint))' }}>
          <Trophy style={{ width: 15, height: 15 }} aria-hidden />
        </span>
        <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--mint, var(--mint))' }}>Paid in full</span>
      </div>
    )
  }
  const why = !allowed ? perms.reason(capability!) : !online ? OFFLINE_ACTION_REASON : null
  return (
    <div data-testid="drawer-cta" style={{ width: '100%' }}>
      <Button
        variant="primary"
        size="hero"
        fullWidth
        leadingIcon={<NextIcon style={{ width: 18, height: 18 }} />}
        trailingIcon={<ArrowRight style={{ width: 18, height: 18 }} />}
        onClick={() => onAdvance(record.id)}
        disabled={isAdvancing || !!why}
        loading={isAdvancing}
        aria-describedby={why ? 'drawer-cta-why' : undefined}
      >
        {currentStage.nextCta}
      </Button>
      <p id={why ? 'drawer-cta-why' : undefined} style={{ fontSize: 12, textAlign: 'center', color: 'var(--midnight-text3)', marginTop: 6 }}>
        {why ?? nextStepHint(record.stage)}
      </p>
    </div>
  )
}

// ─── DetailDrawer ─────────────────────────────────────────────────────────────

/** Side panel from here up; a sheet below (UX-060 — was 1440). */
export const DRAWER_PANEL_MIN_WIDTH = 1024

/**
 * A /work/<id> link whose record isn't here yet (UX-029): the drawer opens at
 * once with a skeleton while the record loads, or says why it can't load
 * (offline: "You're offline" with Try again, never "may have been deleted").
 */
export interface DrawerPending {
  /** Set when the record couldn't be loaded; omitted while it is loading. */
  error?: unknown
  onRetry?: () => void
}

interface DetailDrawerProps extends DrawerActions {
  costingFixture?: boolean
  inspectionFixture?: boolean
  record: Record_ | null
  /** Shown while `record` is null: a linked record still loading, or failed. */
  pending?: DrawerPending | null
  onClose: () => void
  onAdvance: (id: string) => void
  /** When true, the advance CTA is disabled and shows a loading spinner. */
  isAdvancing?: boolean
}

function PendingBody({ pending }: { pending: DrawerPending }) {
  if (pending.error !== undefined) {
    return (
      <div className="ds-drawer-body" data-testid="drawer-pending-error">
        <ErrorState error={pending.error} action="open this work" forbiddenWhat="see this work" onRetry={pending.onRetry} />
      </div>
    )
  }
  return (
    <div className="ds-drawer-body" aria-busy="true" data-testid="drawer-pending">
      <span className="sr-only">Loading this work…</span>
      <span aria-hidden className="shimmer" style={{ display: 'block', height: 18, width: '60%', borderRadius: 8 }} />
      <span aria-hidden className="shimmer" style={{ display: 'block', height: 44, borderRadius: 9999 }} />
      <span aria-hidden className="shimmer" style={{ display: 'block', height: 64, borderRadius: 20 }} />
      <span aria-hidden className="shimmer" style={{ display: 'block', height: 220, borderRadius: 20 }} />
      <span aria-hidden className="shimmer" style={{ display: 'block', height: 140, borderRadius: 20 }} />
    </div>
  )
}

/** Panel exit: shorter than the way in, ease-in (UX-117). */
export const DRAWER_EXIT_MS = 180

export function DetailDrawer({
  record: recordProp,
  costingFixture = false,
  inspectionFixture = false,
  pending = null,
  onClose,
  onAdvance,
  isAdvancing = false,
  ...actions
}: DetailDrawerProps) {
  const fixture = costingFixture || inspectionFixture
  const isPhone = useIsMobile(768)
  const reduced = useReducedMotion()
  const showMoney = useShowMoney()
  const perms = usePermissions()
  const [isPanel, setIsPanel] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth >= DRAWER_PANEL_MIN_WIDTH,
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = () => setIsPanel(window.innerWidth >= DRAWER_PANEL_MIN_WIDTH)
    window.addEventListener('resize', handler, { passive: true })
    return () => window.removeEventListener('resize', handler)
  }, [])

  // ── Exit (UX-117 drawer close latency). Closing navigates, and the Work
  // board re-renders for ~200ms before `record` goes null, so the drawer sat
  // fully visible and then hard-cut. Now Close starts the exit at once
  // (slide out / sheet down), the navigation runs a frame later while the
  // compositor animates, and the drawer keeps the record it showed until the
  // exit ends. A record the person just closed is ignored until the parent
  // catches up, so it can't flash back in.
  const [shown, setShown] = useState<Record_ | null>(recordProp)
  const [leaving, setLeaving] = useState(false)
  const dismissedId = useRef<string | null>(null)
  useEffect(() => { if (!recordProp) dismissedId.current = null }, [recordProp])
  const incoming = recordProp && recordProp.id !== dismissedId.current ? recordProp : null
  if (incoming) {
    if (incoming !== shown) setShown(incoming)
    if (leaving) setLeaving(false)
  } else if (shown && !leaving) {
    if (reduced) setShown(null)
    else setLeaving(true)
  }
  useEffect(() => {
    if (!leaving) return
    const t = window.setTimeout(() => { setShown(null); setLeaving(false) }, DRAWER_EXIT_MS)
    return () => window.clearTimeout(t)
  }, [leaving])

  const close = useCallback(() => {
    if (leaving) return
    // A skeleton (no record yet) has nothing to animate out: close at once.
    if (!shown || reduced) { onClose(); return }
    dismissedId.current = shown.id
    setLeaving(true)
    // Let the first exit frame paint before the board's re-render.
    requestAnimationFrame(() => { window.setTimeout(onClose, 0) })
  }, [shown, leaving, reduced, onClose])

  // The skeleton (UX-029) shows only while no record is on screen; a record
  // being closed keeps its place through the exit.
  const pendingShown = shown ? null : pending
  const open = !!shown || !!pendingShown

  // Panel mode: toasts move to the top so they never cover the pinned CTA.
  useToastsClearOfSidePanel(isPanel && open && !leaving)

  // Panel mode: Escape closes (BottomSheet handles its own).
  useEffect(() => {
    if (!isPanel || !open || leaving) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isPanel, open, leaving, close])

  if (!open) return null
  // The record on screen (kept through the exit), or null on the skeleton.
  // One shell for both, so a drawer that opened on the skeleton stays mounted
  // (no second slide-in) when its record arrives.
  const record = shown
  const pendingFailed = !record && pendingShown?.error !== undefined
  const pendingTitle = pendingFailed ? 'Work' : 'Loading…'
  // Same slot list for the card and (on a sheet) the header, so Edit / Send /
  // Preview can't render twice or disagree about permissions.
  const slots = record
    ? documentSlots(record, actions, {
        showMoney,
        canEditJobs: perms.can('jobs.edit'),
        canSendInvoice: perms.can('invoices.send'),
        canRecordPayment: perms.can('payments.record'),
        canCreateInvoice: perms.can('invoices.create'),
      })
    : null

  // ── Sheet (<1024px): the sheet owns the title, the one close, and the footer.
  if (!isPanel) {
    return (
      <BottomSheet
        open={!leaving}
        onClose={close}
        title={record ? record.customer : pendingTitle}
        description={record ? <ServiceTypeChip name={record.service} color={record.serviceColor} size="xs" /> : undefined}
        dismissible
        showHandle={isPhone}
        snapPoints={['full']}
        initialSnap="full"
        size="lg"
        footer={record && !fixture ? <DrawerCta record={record} onAdvance={onAdvance} isAdvancing={isAdvancing} onSendQuote={actions.onResendQuote} /> : undefined}
        headerExtra={slots && !fixture ? <DrawerHeaderActions slots={slots} /> : undefined}
      >
        {/* Phone and tablet sheets open with the journey collapsed to its one-line
            summary, so the quote, job and money come first (UX-060).
            Document icons are in the sheet header (LF-4): the card that used
            to hold them sits below this fold. */}
        {record && slots ? fixture ? <FixtureBody key={record.id} jobId={record.jobId!} costing={costingFixture} /> : <DrawerBody key={record.id} record={record} actions={actions} slots={slots} inlineActions={false} compact /> : <PendingBody pending={pendingShown!} />}
      </BottomSheet>
    )
  }

  // ── Side panel (≥1024px) ───────────────────────────────────────────────────
  return createPortal(
    <div
      role="complementary"
      aria-labelledby="detail-drawer-heading"
      aria-hidden={leaving || undefined}
      className={cn('ds-drawer-panel', leaving ? 'animate-slide-out-right pointer-events-none' : 'animate-slide-in-right')}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: SIDE_PANEL_WIDTH,
        minWidth: 440,
        zIndex: 40,
        display: 'flex',
        flexDirection: 'column',
        background: 'color-mix(in srgb, rgb(var(--fg-rgb)) 4%, rgb(var(--bg-rgb)))',
        borderLeft: '0.5px solid var(--midnight-border)',
        boxShadow: '-20px 0 60px rgba(0,0,0,0.5)',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '20px 20px 14px', borderBottom: '0.5px solid var(--midnight-border)', flexShrink: 0 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ marginBottom: 8, minHeight: record ? undefined : 24 }}>
            {record && <ServiceTypeChip name={record.service} color={record.serviceColor} />}
          </div>
          <h2 id="detail-drawer-heading" style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.15, color: 'rgb(var(--fg-rgb))', margin: 0, overflowWrap: 'anywhere' }}>
            {record ? record.customer : pendingFailed ? pendingTitle : (
              <>
                <span className="sr-only">{pendingTitle}</span>
                <span aria-hidden className="shimmer" style={{ display: 'inline-block', height: 26, width: 240, maxWidth: '100%', borderRadius: 8, verticalAlign: 'middle' }} />
              </>
            )}
          </h2>
        </div>
        <button type="button" onClick={close} aria-label="Close" className="ds-drawer-close">
          <X style={{ width: 18, height: 18 }} aria-hidden />
        </button>
      </div>

      <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: 20 }}>
        {record && slots ? fixture ? <FixtureBody key={record.id} jobId={record.jobId!} costing={costingFixture} /> : <DrawerBody key={record.id} record={record} actions={actions} slots={slots} inlineActions compact={false} /> : <PendingBody pending={pendingShown!} />}
      </div>

      {/* SafeAreaBottom composes env(safe-area-inset-bottom); never hand-roll it. */}
      {record && !fixture && (
        <SafeAreaBottom
          extra={20}
          style={{ borderTop: '0.5px solid var(--midnight-border)', paddingTop: 16, paddingLeft: 20, paddingRight: 20, background: 'color-mix(in srgb, rgb(var(--fg-rgb)) 4%, rgb(var(--bg-rgb)))', flexShrink: 0 }}
        >
          <DrawerCta record={record} onAdvance={onAdvance} isAdvancing={isAdvancing} onSendQuote={actions.onResendQuote} />
        </SafeAreaBottom>
      )}
    </div>,
    document.body,
  )
}
